import React, { useState, useEffect, Fragment } from 'react'
import PropTypes from 'prop-types'
import SystemMessage from './SystemMessage'
import DangerousInnerHTML from './DangerousInnerHTML'
import { PageLoader } from './loaders'

import { connect } from 'react-redux'
import {
  ModStyledContent,
  StyledPdfIframe,
} from 'portal-modules/common/components/atoms'

const RequestContent = props => {
  const [content, setContent] = useState(props.children || '')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(true)
  const { property_id, cid, local, isTermsPage, isPrivacyPage } = props

  // Handle Link to Scroll
  const handleLinkToScroll = event => {
    // Prevent default behavior
    event.preventDefault()
    // Get the href attribute of the clicked link
    const targetId = event.target.getAttribute('data-target')
    // Find the target container
    const targetElement = document.getElementById(targetId)
    if (targetElement) {
      // Scroll to the target container smoothly
      targetElement.scrollIntoView({ behavior: 'smooth' })
    }
  }

  // To attach events and access elements in HTML content received as a response,
  // we utilized document.querySelector instead of refs, as refs cannot be attached.
  const toggleEventListeners = attach => {
    const listenerFunction = attach ? 'addEventListener' : 'removeEventListener'
    document.querySelectorAll('.linkTo').forEach(link => {
      link[listenerFunction]('click', handleLinkToScroll)
    })
  }

  // Call the toggleEventListeners function with 'true' to attach event listeners when the component mounts
  useEffect(() => {
    return () => {
      // Remove event listeners when the component unmounts
      toggleEventListeners(false)
    }
  }, [])

  const fetchContent = async () => {
    try {
      const content = await props.requestContent(property_id, cid, local)
      setContent(content)
      // Explicitly call this function over here because we need to attach event after content gets loaded
      toggleEventListeners(true)
      setLoading(false)
    } catch (err) {
      console.log(err)
      setError(__('There was a problem loading the content.'))
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!content) {
      fetchContent()
    } else {
      setLoading(false)
    }
  }, [])

  const isHTML = content.match(/(<([^>]+)>)/gi)
  const isPDF = content.slice(content.length - 3, content.length) === 'pdf'
  return (
    <ModStyledContent 
      tabIndex='0' 
      fontStyle={isTermsPage || isPrivacyPage ? 'normal' : ''} 
      isTermsPage={isTermsPage}
      isPrivacyPage={isPrivacyPage}
    >
      {!!content &&
        (typeof content === 'string' ? (
          !isHTML ? (
            isPDF ? (
              <Fragment>
                <StyledPdfIframe src={content} />
              </Fragment>
            ) : (
              __('{%s, 0}', [content])
            )
          ) : (
            <DangerousInnerHTML>{content}</DangerousInnerHTML>
          )
        ) : (
          content
        ))}
      {!!error && <SystemMessage type='error'>{error}</SystemMessage>}
      <PageLoader show={loading} />
    </ModStyledContent>
  )
}

RequestContent.propTypes = {
  /** You can directly pass some content to show to the users */
  children: PropTypes.oneOfType([PropTypes.string, PropTypes.any]),
  /** An api call method (get method) to execute to fetch fresh content */
  requestContent: PropTypes.func,
  property_id: PropTypes.number,
  cid: PropTypes.number,
  /** Flag to indicate if this is the Terms page for accent color styling */
  isTermsPage: PropTypes.bool,
  /** Flag to indicate if this is the Privacy page for accent color styling */
  isPrivacyPage: PropTypes.bool,
}

RequestContent.defaultProps = {
  isTermsPage: false,
  isPrivacyPage: false,
}

export default connect(({ user, accountSettings }) => ({
  property_id: (user && user.propertyId) || '',
  cid: (user && user.cid) || '',
  local: accountSettings?.language_preference,
}))(RequestContent)
